<script lang="ts" setup>
import { watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAgeGate } from '@/composables/useAgeGate'
import { theme } from '@/config/brands'

const { showAgeGate, confirmAgeGate } = useAgeGate()
const router = useRouter()
const route = useRoute()

// 内容门控页面;弹窗展示期间通过浏览器返回/导航链接离开这些页面时,自动关闭弹窗
// (去往门控页时保持,由目标页 checkAgeGate 权威裁决重新弹出或关闭)
const GATED_ROUTES = ['book', 'chapter']

watch(() => route.name, (name) => {
  if (showAgeGate.value && !GATED_ROUTES.includes(name as string))
    showAgeGate.value = false
})

// PC 端暂无 i18n 运行时,遵循现状使用静态英文文案(与 H5 端文案一致)
const texts = {
  title: 'Are you 18 years or older?',
  desc: 'This content is intended for adults only.',
  confirm: 'Yes, I\'m 18 or older',
  cancel: 'No, go back',
}

const brandBg = theme.primaryGradient || theme.primaryColor
const modalBg = theme.components?.BasicModal?.background || '#FFFFFF'
const textPrimary = theme.textPrimary
const textSecondary = theme.textSecondary
const cardBg = theme.bgCard
const borderColor = theme.borderPrimary

/**
 * 点击 No:关闭弹窗并返回上一页,不记录确认状态
 * - 无上级历史(广告/分享直达):回首页,不会退出站点
 * - 返回后的页面若也是 18+ 内容,由其自身的 checkAgeGate 重新弹出
 */
function onReject() {
  showAgeGate.value = false
  const back = window.history.state?.back
  if (!back) {
    router.replace('/')
    return
  }
  router.back()
}
</script>

<template>
  <Teleport to="body">
    <Transition name="age-gate-fade">
      <!-- 品牌色用内联 style 而非 CSS v-bind:CSS 变量挂在组件根节点,Teleport 子树取不到 -->
      <div v-if="showAgeGate" class="age-gate-mask">
        <div class="age-gate-panel" :style="{ background: modalBg }">
          <div class="age-gate-badge" :style="{ background: brandBg }">
            18+
          </div>
          <p class="age-gate-title" :style="{ color: textPrimary }">
            {{ texts.title }}
          </p>
          <p class="age-gate-desc" :style="{ color: textSecondary }">
            {{ texts.desc }}
          </p>
          <button class="age-gate-btn age-gate-btn-yes" :style="{ background: brandBg }" @click="confirmAgeGate">
            {{ texts.confirm }}
          </button>
          <button
            class="age-gate-btn age-gate-btn-no"
            :style="{ background: cardBg, color: textSecondary, border: `1px solid ${borderColor}` }"
            @click="onReject"
          >
            {{ texts.cancel }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.age-gate-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  /* 高于现有业务弹窗(1000),低于 toast(9999) */
  z-index: 1100;
}

.age-gate-panel {
  width: 320px;
  border-radius: 20px;
  padding: 26px 22px 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0px 12px 32px 0px rgba(0, 0, 0, 0.16);
}

.age-gate-badge {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.age-gate-title {
  margin: 16px 0 0;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
}

.age-gate-desc {
  margin: 8px 0 0;
  font-size: 13px;
  text-align: center;
}

.age-gate-btn {
  display: block;
  width: 100%;
  height: 42px;
  border-radius: 60px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;
  font-family: inherit;
}
.age-gate-btn:hover { opacity: 0.9; }

.age-gate-btn-yes {
  margin-top: 20px;
  color: #FFFFFF;
}

.age-gate-btn-no {
  margin-top: 12px;
  font-weight: 500;
}

.age-gate-fade-enter-active,
.age-gate-fade-leave-active {
  transition: opacity 0.3s;
}
.age-gate-fade-enter-from,
.age-gate-fade-leave-to {
  opacity: 0;
}
</style>
